import { NavLink, Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useAuthStore } from '../store/auth';
import { useBranchStore } from '../store/branch';
import { authApi, branchesApi, billingApi } from '../api';
import toast from 'react-hot-toast';

/**
 * MainLayout â€” Option A app shell
 *
 * Structure:
 *   â”Œâ”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”
 *   â”‚  [sidebar: 240px]   â”‚   [topbar: 56px]              â”‚
 *   â”‚                     â”œâ”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”¤
 *   â”‚                     â”‚                               â”‚
 *   â”‚                     â”‚   [outlet â€” page container]   â”‚
 *   â”‚                     â”‚                               â”‚
 *   â””â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”˜
 *
 * Sidebar: cream canvas, ink-900 text, sage accent for active rail,
 *          grouped by business mental model, collapsible sections,
 *          workspace header + profile footer.
 *
 * Topbar: breadcrumb derived from route, global search trigger,
 *         branch selector placeholder, alerts bell, avatar menu.
 */

interface NavItem {
  label: string;
  path?: string;
  icon?: ReactNode;
  children?: NavItem[];
}

// ============================================================
// Nav config â€” grouped by business mental model (per blueprint Â§12)
// ============================================================
const NAV: NavItem[] = [
  {
    label: 'Overview',
    icon: <HomeIcon />,
    children: [
      { label: 'Dashboard', path: '/dashboard' },
      { label: 'Reports', path: '/reports' },
    ],
  },
  {
    label: 'Commerce',
    icon: <CartIcon />,
    children: [
      { label: 'POS register', path: '/pos' },
      { label: 'Shifts', path: '/pos/shifts' },
      { label: 'Sales history', path: '/pos/sales' },
      { label: 'Returns', path: '/pos/returns' },
      { label: 'Gift cards', path: '/pos/gift-cards' },
      { label: 'Customers', path: '/sales/customers' },
      { label: 'Sales orders', path: '/sales/orders' },
      { label: 'Quotes', path: '/sales/quotes' },
      { label: 'Leads', path: '/sales/leads' },
      { label: 'Pipeline', path: '/sales/pipeline' },
      { label: 'Promotions', path: '/sales/promotions' },
      { label: 'Segments', path: '/sales/segments' },
      { label: 'Commissions', path: '/sales/commissions' },
    ],
  },
  {
    label: 'Inventory',
    icon: <BoxIcon />,
    children: [
      { label: 'Products', path: '/inventory/products' },
      { label: 'Catalog setup', path: '/inventory/catalog-setup' },
      { label: 'Variants', path: '/inventory/variants' },
      { label: 'Stock levels', path: '/inventory/stock' },
      { label: 'Adjustments', path: '/inventory/adjustments' },
      { label: 'Transfers', path: '/inventory/transfers' },
      { label: 'Stock counts', path: '/inventory/counts' },
      { label: 'Serials', path: '/inventory/serials' },
      { label: 'Expiring batches', path: '/inventory/expiring' },
      { label: 'Reorder', path: '/inventory/reorder' },
      { label: 'Supplier returns', path: '/inventory/rma' },
      { label: 'Locations', path: '/inventory/locations' },
      { label: 'Bulk / Labels', path: '/inventory/bulk' },
    ],
  },
  {
    label: 'Purchasing',
    icon: <TruckIcon />,
    children: [
      { label: 'Suppliers', path: '/purchase/suppliers' },
      { label: 'Onboarding', path: '/purchase/onboarding' },
      { label: 'Scorecards', path: '/purchase/scorecard' },
      { label: 'Requisitions', path: '/purchase/requisitions' },
      { label: 'RFQs', path: '/purchase/rfqs' },
      { label: 'Purchase orders', path: '/purchase/orders' },
      { label: 'Goods receipts', path: '/purchase/grns' },
      { label: 'Return to vendor', path: '/purchase/rtv' },
    ],
  },
  {
    label: 'Finance',
    icon: <LedgerIcon />,
    children: [
      { label: 'Chart of accounts', path: '/accounting/accounts' },
      { label: 'Vouchers', path: '/accounting/vouchers' },
      { label: 'Journal entries', path: '/accounting/journal-entries' },
      { label: 'AR invoices', path: '/accounting/ar/invoices' },
      { label: 'AR aging', path: '/accounting/ar/aging' },
      { label: 'AP invoices', path: '/accounting/ap/invoices' },
      { label: 'AP aging', path: '/accounting/ap/aging' },
      { label: 'Payment runs', path: '/accounting/ap/payment-runs' },
      { label: 'Period close', path: '/accounting/period-close' },
      { label: 'Bank reconciliation', path: '/accounting/bank-rec' },
      { label: 'FX', path: '/accounting/fx' },
      { label: 'Tax', path: '/accounting/tax' },
      { label: 'Reports', path: '/accounting/reports' },
    ],
  },
  {
    label: 'People',
    icon: <UsersIcon />,
    children: [
      { label: 'Employees', path: '/hrm/employees' },
      { label: 'Attendance', path: '/hrm/attendance' },
      { label: 'Leave', path: '/hrm/leave' },
      { label: 'Payroll', path: '/hrm/payroll' },
      { label: 'Performance', path: '/hrm/performance' },
      { label: 'Recruitment', path: '/hrm/recruitment' },
      { label: 'Training', path: '/hrm/training' },
    ],
  },
  {
    label: 'Insights',
    icon: <ChartIcon />,
    children: [
      { label: 'Sales & Inventory', path: '/reports/sales-inventory' },
      { label: 'Finance & HR', path: '/reports/finance-hr' },
      { label: 'Exports & Schedules', path: '/reports/exports' },
    ],
  },
  {
    label: 'Administration',
    icon: <GearIcon />,
    children: [
      { label: 'Users', path: '/admin/users' },
      { label: 'Roles', path: '/admin/roles' },
      { label: 'Branches', path: '/admin/branches' },
      { label: 'Settings', path: '/admin/settings' },
      { label: 'Billing & plan', path: '/billing' },
      { label: 'Audit log', path: '/admin/audit-log' },
      { label: 'Platform', path: '/admin/platform' },
      { label: 'Plan management', path: '/admin/plans' },
    ],
  },
];

// Flat index of path â†’ {label, groupLabel} for breadcrumb lookup
const PATH_INDEX: Record<string, { label: string; group: string }> = {};
for (const group of NAV) {
  if (group.path) PATH_INDEX[group.path] = { label: group.label, group: group.label };
  for (const child of group.children ?? []) {
    if (child.path) PATH_INDEX[child.path] = { label: child.label, group: group.label };
  }
}

// ============================================================
// MainLayout â€” top-level
// ============================================================
export function MainLayout() {
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const navigate = useNavigate();
  const location = useLocation();

  const logout = async () => {
    try {
      if (refreshToken) await authApi.logout(refreshToken);
    } catch {}
    clear();
    toast.success('Signed out');
    navigate('/login');
  };

  // Only customer display should be fullscreen, not POS register
  const isKioskMode =
    location.pathname.startsWith('/pos/customer-display');

  return (
    <div className="flex h-screen bg-canvas overflow-hidden">
      {!isKioskMode && <Sidebar user={user} onLogout={logout} />}

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {!isKioskMode && <Topbar user={user} onLogout={logout} />}
        {!isKioskMode && <TrialBanner />}

        <main className="flex-1 min-h-0 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// ============================================================
// Sidebar
// ============================================================
function Sidebar({ user, onLogout }: { user: any; onLogout: () => void }) {
  const location = useLocation();

  // Remember which groups are expanded. Auto-expand the group containing the active route.
  const activeGroup = useMemo(() => {
    for (const group of NAV) {
      if (group.path === location.pathname) return group.label;
      if (group.children?.some((c) => c.path === location.pathname)) return group.label;
    }
    return null;
  }, [location.pathname]);

  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const s = new Set<string>();
    if (activeGroup) s.add(activeGroup);
    else s.add('Overview');
    return s;
  });

  // If the active group changes (user navigates via URL/link), auto-expand it.
  useEffect(() => {
    if (activeGroup) {
      setExpanded((prev) => {
        if (prev.has(activeGroup)) return prev;
        const next = new Set(prev);
        next.add(activeGroup);
        return next;
      });
    }
  }, [activeGroup]);

  const toggle = (label: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  return (
    <aside className="w-60 bg-canvas-elevated border-r border-ink-200 flex flex-col flex-shrink-0 shadow-sm">
      {/* Brand header */}
      <div className="h-14 px-4 flex items-center border-b border-ink-200">
        <Link to="/dashboard" className="flex items-center group">
          <img
            src={`${import.meta.env.BASE_URL}logos/OmniRetail_Emerald_Logo_Horizontal.png`}
            alt="OmniRetail"
            className="h-7 w-auto object-contain"
            style={{ maxWidth: 148 }}
          />
        </Link>
      </div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {NAV.map((group) => {
          const isExpanded = expanded.has(group.label);
          const isActiveGroup = group.label === activeGroup;
          return (
            <div key={group.label} className="mb-0.5">
              <button
                onClick={() => toggle(group.label)}
                className={clsx(
                  'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm font-semibold transition-colors',
                  isActiveGroup
                    ? 'bg-sage-50 text-sage-700'
                    : 'text-ink-700 hover:bg-canvas-subtle hover:text-ink-900'
                )}
              >
                <span className={clsx('flex-shrink-0', isActiveGroup ? 'text-sage-600' : 'text-ink-400')}>
                  {group.icon}
                </span>
                <span className="flex-1 text-left">{group.label}</span>
                <svg
                  width="12" height="12" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round"
                  className={clsx('transition-transform duration-200 ease-out-expo',
                                   isActiveGroup ? 'text-sage-500' : 'text-ink-400',
                                   isExpanded && 'rotate-90')}
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>

              {isExpanded && group.children && (
                <div className="mt-0.5 ml-3 pl-3 border-l border-ink-200 py-0.5">
                  {group.children.map((child) => (
                    <NavLink
                      key={child.path}
                      to={child.path!}
                      className={({ isActive }) =>
                        clsx(
                          'block px-2.5 py-1.5 rounded-md text-sm transition-colors relative',
                          isActive
                            ? 'bg-sage-50 text-sage-700 font-semibold'
                            : 'text-ink-600 hover:text-ink-900 hover:bg-canvas-subtle/60'
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && (
                            <span
                              className="absolute -left-3.5 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-sage-500 rounded-r-sm"
                              aria-hidden
                            />
                          )}
                          {child.label}
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Profile footer */}
      <div className="border-t border-ink-200 p-3">
        <ProfileMenu user={user} onLogout={onLogout} />
      </div>
    </aside>
  );
}

// ============================================================
// Profile menu (sidebar footer)
// ============================================================
function ProfileMenu({ user, onLogout }: { user: any; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const initials = (user?.fullName || user?.email || '?')
    .split(/[\s@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s: string) => s[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-canvas-subtle transition-colors"
      >
        <div className="w-8 h-8 rounded-full bg-sage-100 text-sage-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className="text-sm font-semibold text-ink-900 truncate">{user?.fullName ?? 'â€”'}</div>
          <div className="text-xs text-ink-500 truncate">{user?.email}</div>
        </div>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          className="text-ink-400 flex-shrink-0">
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </button>

      {open && (
        <div className="absolute bottom-full mb-2 left-0 right-0 bg-canvas-elevated border border-ink-200 rounded-xl shadow-lg overflow-hidden py-1">
          <div className="px-3 py-2 border-b border-ink-100">
            <div className="text-xs text-ink-500">Signed in as</div>
            <div className="text-sm font-medium text-ink-900 truncate">{user?.email}</div>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {user?.roles?.slice(0, 3).map((r: string) => (
                <span key={r} className="badge bg-canvas-subtle text-ink-700 ring-1 ring-inset ring-ink-200">
                  {r}
                </span>
              ))}
            </div>
          </div>
          <Link
            to="/admin/settings"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 text-sm text-ink-700 hover:bg-canvas-subtle"
          >
            Settings
          </Link>
          <button
            onClick={() => { setOpen(false); onLogout(); }}
            className="w-full text-left px-3 py-2 text-sm text-danger hover:bg-red-50"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Topbar â€” breadcrumb + actions
// ============================================================
function Topbar({ user, onLogout: _onLogout }: { user: any; onLogout: () => void }) {
  const location = useLocation();
  const crumbs = useMemo(() => {
    const entry = PATH_INDEX[location.pathname];
    if (!entry) return [{ label: 'Home' }];
    if (entry.label === entry.group) return [{ label: entry.label }];
    return [{ label: entry.group, muted: true }, { label: entry.label }];
  }, [location.pathname]);

  return (
    <header className="h-14 bg-canvas-elevated border-b border-ink-200 flex items-center px-6 gap-4 flex-shrink-0 shadow-xs">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm min-w-0">
        {crumbs.map((c, i) => (
          <span key={i} className="flex items-center gap-1.5 min-w-0">
            {i > 0 && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                className="text-ink-300 flex-shrink-0">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            )}
            <span className={clsx(
              'truncate',
              (c as any).muted ? 'text-ink-500' : 'text-ink-900 font-semibold'
            )}>
              {c.label}
            </span>
          </span>
        ))}
      </nav>

      <div className="flex-1" />

      

      {/* Branch selector */}
      <BranchSelector />

      {/* Alerts */}
      <button
        onClick={() => toast('No new alerts', { icon: 'ðŸ””' })}
        className="relative p-2 rounded-pill hover:bg-canvas-subtle transition-colors text-ink-600"
        aria-label="Alerts"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>
      </button>

      {/* User avatar (mirrors sidebar) */}
      <div className="w-8 h-8 rounded-full bg-sage-100 text-sage-700 flex items-center justify-center text-xs font-semibold">
        {(user?.fullName || user?.email || '?')
          .split(/[\s@]+/).filter(Boolean).slice(0, 2)
          .map((s: string) => s[0]?.toUpperCase() ?? '').join('')}
      </div>
    </header>
  );
}

// ============================================================
// Branch selector â€” placeholder dropdown
// ============================================================
function TrialBanner() {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['billing', 'overview'],
    queryFn: () => billingApi.overview(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const sub = (q.data as any)?.subscription;
  if (!sub) return null;

  // Show only when it's worth nudging: on trial, or past_due/canceled.
  const onTrial = sub.on_trial;
  const needsAttention = sub.status === 'past_due' || sub.status === 'canceled';
  if (!onTrial && !needsAttention) return null;

  const bg = needsAttention ? '#fef2f2' : '#fffbeb';
  const fg = needsAttention ? '#b91c1c' : '#92400e';
  const msg = needsAttention
    ? `Your subscription is ${sub.status}. Reactivate to keep full access.`
    : `You're on a free trial — ${sub.trial_days_left} day${sub.trial_days_left === 1 ? '' : 's'} left.`;

  return (
    <div style={{ background: bg, color: fg, padding: '8px 20px', display: 'flex', alignItems: 'center',
      justifyContent: 'center', gap: 14, fontSize: 13, fontWeight: 600, borderBottom: `1px solid ${fg}22` }}>
      <span>{msg}</span>
      <button onClick={() => navigate('/billing')}
        style={{ padding: '4px 14px', borderRadius: 8, border: 'none', background: fg, color: '#fff',
          fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
        {needsAttention ? 'Manage billing' : 'Subscribe'}
      </button>
    </div>
  );
}

function BranchSelector() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { branchId, branchName, setBranch } = useBranchStore();
  const qc = useQueryClient();

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const branchesQuery = useQuery({
    queryKey: ['branches', 'selector'],
    queryFn: () => branchesApi.list(),
    staleTime: 5 * 60 * 1000,
  });

  const choose = (id: string | null, name: string) => {
    setBranch(id, name);
    setOpen(false);
    // Branch-scoped lists key off the X-Branch-Id header, not the queryKey,
    // so invalidate everything to force a refetch under the new branch.
    qc.invalidateQueries();
  };

  const options = [{ id: null as string | null, name: 'All branches' }]
    .concat((branchesQuery.data ?? []).map((b: any) => ({ id: b.id, name: b.name })));

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-pill bg-canvas-subtle hover:bg-ink-100 transition-colors text-sm text-ink-700"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" className="text-ink-500">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
        <span>{branchName}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          className="text-ink-400">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-48 bg-canvas-elevated border border-ink-200 rounded-xl shadow-lg overflow-hidden py-1 z-50">
          {options.map((b) => (
            <button
              key={b.id ?? 'all'}
              onClick={() => choose(b.id, b.name)}
              className={clsx(
                'w-full text-left px-3 py-2 text-sm hover:bg-canvas-subtle',
                (b.id ?? null) === (branchId ?? null) ? 'text-ink-900 font-semibold' : 'text-ink-700'
              )}
            >
              {b.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Icons
// ============================================================
const iconProps = {
  width: 16, height: 16, viewBox: '0 0 24 24',
  fill: 'none', stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};
function HomeIcon() { return <svg {...iconProps}><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>; }
function CartIcon() { return <svg {...iconProps}><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>; }
function BoxIcon() { return <svg {...iconProps}><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>; }
function TruckIcon() { return <svg {...iconProps}><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>; }
function LedgerIcon() { return <svg {...iconProps}><path d="M4 4h16v16H4z"/><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="4" x2="10" y2="20"/></svg>; }
function UsersIcon() { return <svg {...iconProps}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>; }
function GlobeIcon() { return <svg {...iconProps}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>; }
function ChartIcon() { return <svg {...iconProps}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>; }
function GearIcon() { return <svg {...iconProps}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>; }
